import { makeVar } from '@apollo/client'
import { CurrentUserInfo } from '@deltek/specification-client/core/Models/currentUserInfo'
import { User } from '@deltek/specification-client/core/Models/user'
import { v4 as uuidv4 } from 'uuid'
import { LABEL_MISSING_USER_NAME, LABEL_MISSING_USER_ROLE } from './ListingNotes.constants'
import {
    NoteType,
    PDNResponseType,
    PDNStoreType,
    ProductDetailNoteData,
} from './ListingNotes.types'

export const DEFAULT_PDN_STORE = {
    listingId: '',
    tempNotes: {},
    count: 0,
    notes: {},
    currentUser: {},
    noteTypes: [],
    isLoaded: false,
}

export const pdnReactiveVar = makeVar<PDNStoreType>(DEFAULT_PDN_STORE)
export const usersReactiveVar = makeVar<string[]>([])

export const unreadNotesCount = (userId: string) => {
    return Object.values(pdnReactiveVar().notes).reduce((acc: number, note) => {
        if (!note?.seenByUserId?.includes(userId)) {
            acc += 1
        }
        return acc
    }, 0)
}

export const totalNotesCount = () => {
    return (
        Number(pdnReactiveVar().count) +
        Object.values(pdnReactiveVar().tempNotes).length
    )
}

export const setListingId = (listingId: string) => {
    const temp = pdnReactiveVar()
    pdnReactiveVar({ ...temp, listingId })
}

export const setIsLoaded = () => {
    const temp = pdnReactiveVar()
    pdnReactiveVar({ ...temp, isLoaded: true })
}

export const setNotes = (notes: any, queryString: string) => {
    const temp = pdnReactiveVar()

    pdnReactiveVar({
        ...temp,
        count: notes.totalResult ?? 0,
        notes: notes.result.reduce(
            (
                acc: Record<string, Partial<ProductDetailNoteData>>,
                {
                    id,
                    listingId,
                    content,
                    reason,
                    productDetailNoteType,
                    seenByUserId,
                    metadata,
                }: PDNResponseType
            ) => {
                let longDescription = content
                if (content.includes('contentEditable={false}')) {
                    const splitContent = content.split('a href=')
                    const listingIdString =
                        splitContent?.[1]?.split('\u0027') ?? ''
                    longDescription = `${splitContent?.[0]}a href=\u0027${
                        listingIdString[1]
                    }?${queryString ?? ''}\u0027${listingIdString[2]}`
                }

                return {
                    ...acc,
                    [id]: {
                        id: id,
                        listingId: listingId,
                        longDescription,
                        noteType: productDetailNoteType,
                        reason,
                        seenByUserId,
                        createdDate: metadata.created,
                        userId: metadata.creatorId,
                        userFullName: metadata?.createdBy?.profileInfo 
                            ? `${metadata.createdBy.profileInfo.firstName} ${metadata.createdBy.profileInfo.lastName}` 
                            : LABEL_MISSING_USER_NAME,
                        userRole: metadata?.createdBy?.role ?? LABEL_MISSING_USER_ROLE,
                        isTemporary: false,
                    },
                }
            },
            temp.notes
        ),
    })
}

export const addNoteData = ({
    id,
    listingId,
    content,
    reason,
    productDetailNoteType,
    seenByUserId,
    metadata,
}: PDNResponseType) => {
    let temp = pdnReactiveVar()
    const notesData = { ...temp.notes }

    if (temp.tempNotes[productDetailNoteType]) {
        let tempNotes = temp.tempNotes
        delete tempNotes[productDetailNoteType]

        temp.tempNotes = tempNotes
    }

    pdnReactiveVar({
        ...temp,
        count: temp.count + 1,
        notes: {
            ...notesData,
            [id]: {
                id,
                listingId: listingId,
                longDescription: content,
                noteType: productDetailNoteType as NoteType,
                reason,
                seenByUserId,
                createdDate: metadata.created,
                userId: metadata.creatorId,
                isTemporary: false,
                userFullName: `${metadata?.createdBy?.profileInfo?.firstName} ${metadata?.createdBy?.profileInfo?.lastName}`,
                userRole: metadata?.createdBy?.role,
            },
        },
    })
}

export const updateNoteData = ({
    id,
    listingId,
    content,
    reason,
    seenByUserId,
    metadata,
    productDetailNoteType,
}: PDNResponseType) => {
    const temp = pdnReactiveVar()
    const tempNotes = temp.notes
    pdnReactiveVar({
        ...temp,
        notes: {
            ...tempNotes,
            [id]: {
                id,
                listingId: listingId,
                longDescription: content,
                noteType: productDetailNoteType,
                seenByUserId,
                reason,
                createdDate: metadata.created,
                userId: metadata.creatorId,
                isTemporary: false,
            } as Partial<ProductDetailNoteData>,
        },
    })
}

export const removeNoteData = (id: string) => {
    const temp = pdnReactiveVar()
    let tempNotes = { ...temp.notes }

    delete tempNotes[id]

    pdnReactiveVar({
        ...temp,
        notes: tempNotes,
        count: temp.count - 1,
    })
}

export const setPDNUserList = (users: string[]) => {
    usersReactiveVar(users)
}

export const setTempNotes = ({
    noteType,
    longDescription,
    reason,
}: Record<string, string | NoteType>) => {
    const temp = pdnReactiveVar()
    pdnReactiveVar({
        ...temp,
        tempNotes: {
            ...temp.tempNotes,
            [noteType]: {
                id: uuidv4(),
                listingId: temp.listingId,
                longDescription,
                noteType: noteType as NoteType,
                seenByUserId: [],
                createdDate: new Date().toLocaleString('en-US'),
                userName: temp.currentUser?.fullName ?? '',
                userId: temp.currentUser?.id ?? '',
                userRole: temp.currentUser?.role ?? '',
                isTemporary: true,
                reason,
            },
        },
    })
}

export const removeTempNote = (id: string) => {
    const temp = pdnReactiveVar()
    const tempNotes = { ...temp.tempNotes }

    const noteType =
        Object.entries(tempNotes ?? {}).find(
            (tempNote) => tempNote[1].id === id
        )?.[0] ?? ''

    delete tempNotes[noteType]

    pdnReactiveVar({
        ...temp,
        tempNotes,
    })
}

export const resetTempNotes = () => {
    const temp = pdnReactiveVar()

    pdnReactiveVar({
        ...temp,
        tempNotes: {},
    })
}

export const resetPDNStore = () => {
    pdnReactiveVar(DEFAULT_PDN_STORE)
}

export const setCurrentUser = ({
    account,
    email,
    firstName,
    lastName,
    kpUserId,
    roles,
}: CurrentUserInfo) => {
    const temp = pdnReactiveVar()
    pdnReactiveVar({
        ...temp,
        currentUser: {
            id: kpUserId,
            userType: account.accountType,
            email,
            fullName: `${firstName} ${lastName}`,
            role: (roles[0] as User['role']) ?? undefined,
        },
    })
}
